import type { DealAnalysis, FieldProvenance } from '../../types';

/**
 * The Phase 1 guard compares against a bare source string; provenance objects
 * carry it in `.source`. Every 'user' check must go through this, or an
 * extraction that upgrades a field to a provenance object would silently strip
 * that field's protection from presets.
 */
export const sourceOf = (v: string | FieldProvenance | undefined): string | undefined =>
  typeof v === 'string' ? v : v?.source;

/**
 * Sources a preset must never overwrite.
 *
 * ⭐⭐ 'user' is hand-typed work. 'extraction' is a figure READ OUT OF THE DOCUMENT, and it
 * outranks a preset default for the same reason a STATED escalation date outranks the
 * anniversary rule: the document is evidence and the preset is a guess about a category of deal.
 *
 * 🚨 THE DEFECT. 258 Bernard Street's landlord insurance - $10,075, derived by the extractor from
 * the OM's own rent roll and marketed NOI, and named in its note - seeded correctly and was then
 * ZEROED the moment the Single-tenant NNN archetype was picked, because that preset carries
 * `other_expenses: 0` and nothing marked the seeded value as evidence. The cap moved 6.75% ->
 * 6.94%, away from the delivered analysis, on a click that looks like it only sets thresholds.
 */
export const PROTECTED_SOURCES = new Set(['user', 'extraction']);

/**
 * Stamp fields the DOCUMENT supplied as `extraction`, so `applyPreset` refuses them.
 *
 * 🚨🚨 THE LATCH THIS REPLACES COST A REAL DEAL. It read `if (!next[k]) next[k] = 'extraction'`,
 * so the document could only claim a field that nothing else had stamped. On 13368 Main Street the
 * Single-tenant NNN archetype - the one preset carrying all six expense fields at 0 - stamped them
 * `archetype_preset` first, and from then on the OM's CAM $54,022, taxes $46,523 and insurance
 * $5,068 could never take them back. Switching archetype did not help: a field stamped BY a preset
 * is not protected FROM a preset. The model collected $111,114 of recoveries against $0 of cost -
 * NOI $349,318 against the OM's $269,254, a 7.46% cap against a stated 5.75%, and a GO.
 *
 * ⭐⭐⭐ FIRST WRITER WINS IS THE WRONG RULE WHEN ONE OF THE WRITERS IS THE DOCUMENT. A preset is a
 * guess about a category of deal; the OM is evidence about this one. Only `user` - hand-typed work
 * - outranks it, which is precisely what `PROTECTED_SOURCES` already says.
 */
export function markExtractionSources(
  prev: Record<string, string | FieldProvenance>,
  fields: readonly string[],
): Record<string, string | FieldProvenance> {
  const next = { ...prev };
  for (const k of fields) {
    if (sourceOf(next[k]) === 'user') continue;
    next[k] = 'extraction';
  }
  return next;
}

/**
 * Apply preset values without destroying manual work or extracted evidence. A field whose
 * recorded source is 'user' or 'extraction' is never overwritten; anything else is fair game.
 * Returns new objects so React state updates stay predictable.
 */
export function applyPreset(
  current: Partial<DealAnalysis>,
  preset: Partial<DealAnalysis>,
  sources: Record<string, string | FieldProvenance>,
  origin: string
): { data: Partial<DealAnalysis>; sources: Record<string, string | FieldProvenance> } {
  const data = { ...current };
  const nextSources = { ...sources };

  (Object.keys(preset) as Array<keyof DealAnalysis>).forEach(key => {
    const k = key as string;
    if (PROTECTED_SOURCES.has(sourceOf(nextSources[k]) ?? '')) return;
    (data as Record<string, unknown>)[k] = preset[key];
    nextSources[k] = origin;
  });

  return { data, sources: nextSources };
}
